import { useTranslations } from "next-intl";
import { Card } from "../Card/Card";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { useFields } from "@/providers/FieldsProvider";
import { useContext, useMemo, useState, lazy, Suspense } from "react";
import { useLazyOpen } from "@/hooks/useLazyOpen";
import { Field } from "@/types/Field";
import {
  getMissingRequiredFields,
  RequiredFieldsWarnDialog,
} from "./components/requiredFieldsWarning";
import { WorkflowOverwriteDialog } from "./components/WorkflowOverwriteDialog";
import { useGuestWorkflowPreview } from "@/hooks/useGuestWorkflowPreview";
import {
  isWorkflowOverwriteWarnHidden,
  setWorkflowOverwriteWarnHidden,
} from "@/lib/guests/workflowOverwriteWarnPref";
import {
  OverwritePreview,
  OverwriteEntry,
} from "@/lib/utils/workflow-overwrite-preview";
import { EmailTrigger } from "@/types/enums/EmailTrigger";
import { EventContext } from "@/providers/EventProvider";
import { GuestsContext } from "@/providers/GuestsProvider";
import { WorkflowOverwriteProvider } from "../Inputs/WorkflowFieldBadge";
import { GuestState } from "@/types/enums/GuestState";
import { Guest } from "@/types/Guest";
import { CAPACITY_DECISION_PENDING } from "@/lib/api/errors/ErrorHandler";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import { useUserLocale } from "@/hooks/useUserLocale";
import {
  describeAdditionalFieldViolation,
  type AdditionalFieldErrorCode,
} from "@/lib/additionalFieldConstraints";

import {
  IconAlertTriangle,
  IconLayoutGrid,
  IconLoader2,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import { useEventCompanyRole } from "@/lib/query/hooks/useEventEntitlements";
import { GuestDetailLayoutView } from "../PGuestDetails/layout/GuestDetailLayoutView";
import {
  CreateSurface,
  slugOfCreateSurface,
  useCreateLayout,
} from "../PGuestDetails/layout/createLayoutSurface";
import { EntityDetailLayoutPicker } from "../DetailLayoutConfig/EntityDetailLayoutPicker";
import { EDL_STANDARD } from "@/lib/storage/entityDetailLayoutPref";
import { useRole } from "@/providers/RoleProvider";
import { PDeleteConfirmation } from "../PDeleteConfirmation/PDeleteConfirmation";
import { PlaceholderCompanions } from "./components/PlaceholderCompanions";
import { useInvalidFields } from "@/hooks/useInvalidFields";

/** "New custom field" — used to sit above the Zusatzfelder grid inside the
 *  hardcoded AdditionalFields block. In a layout-driven form a custom field can
 *  live in ANY group, so the affordance moved to the dialog header where it is
 *  layout-independent. Lazy, like it was: the editor is a big chunk. */
// B2 (FE-6): the dialog + the "hidden in N 'All except' roles" toast host.
const loadAdditionalField = () => import("../PAdditionalField/PAdditionalFieldWithRoleHint");
const PAdditionalField = lazy(() =>
  loadAdditionalField().then((m) => ({ default: m.PAdditionalFieldWithRoleHint })),
);


interface CreateGuestBodyProps {
  onClose: () => void;
  onCreation: (obj: CreationFieldsRef) => Promise<void>;
  companion: boolean;
  mainGuest: boolean;
  mainGuestId?: string;
  freeContingent?: number;
  /** Which create lane this dialog is — decides WHICH stored field layout it
   *  renders. Explicit rather than derived from companion/mainGuest: those two
   *  cannot tell the guest-list's "add companion" mount apart from the
   *  companion-REQUEST lane. */
  surface?: CreateSurface;
  /**
   * Companion lane only: create `count` nameless placeholder companions
   * ("TBD 1", "TBD 2", …) instead of filling the form.
   *
   * Passed in rather than built here because the numbering has to continue
   * after the placeholders the host guest ALREADY has, and only the mount that
   * owns the companion list knows those. Absent ⇒ the affordance is not
   * rendered at all, which is what every other create lane wants.
   */
  onCreatePlaceholdersAction?: (count: number) => Promise<void>;
}

export interface CreationFieldsRef {
  guestType: string;
  mainGuest: string;
  // Full DTO of the selected main guest (companion flow). Captured by the
  // GuestSearch picker so the optimistic companion row can render the
  // Verknüpfung cell immediately, before/without the server round-trip.
  mainGuestDTO?: Guest;
  firstName: string;
  lastName: string;
  gender: string;
  title: string;
  company: string;
  language: string;
  email: string;
  jobTitle: string;
  phone: string;
  mobile: string;
  address: string;
  address2: string;
  zipCode: string;
  username: string;
  code: string;
  city: string;
  country: string;
  state: string;
  emailOptIn: string;
  additionalFields?: Record<string, unknown>;
}

export const CreateGuestBody: React.FC<CreateGuestBodyProps> = ({
  onClose,
  onCreation,
  companion,
  mainGuest,
  mainGuestId,
  freeContingent,
  surface = "guestCreate",
  onCreatePlaceholdersAction,
}) => {
  const t = useTranslations("popup-create-guest");
  const t2 = useTranslations("errors");
  const tLayout = useTranslations("guest-details");
  const tUnsaved = useTranslations("unsavedChanges");
  const { allFields } = useFields();
  const navigate = useNavigate();
  const { legacyRoleFloor, role, isArchived, isPlanLocked } = useRole();
  // Optional read — PCreateGuest is also used outside an event-scoped surface
  // (some companion/registrant contexts); the preview just stays inactive there.
  const eventCtx = useContext(EventContext);
  // The create form often runs outside EventProvider (guest-detail popup,
  // companion/registrant flows). GuestsProvider carries the eventId when
  // present; both are optional so the preview just stays inactive otherwise.
  const guestsCtx = useContext(GuestsContext);
  const previewEventId = eventCtx?.event?.eventId || guestsCtx?.eventId;
  // Slots are bought by the OWNER of the company that OWNS the event.
  const eventCompany = useEventCompanyRole(previewEventId ?? null);
  const tGate = useTranslations("planGate");
  const { getOverwrites } = useGuestWorkflowPreview(previewEventId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locale = useUserLocale();
  // Boxes whose text the form does NOT hold (an unstorable email). The Create
  // click below refuses while one exists: the form would otherwise create the
  // guest without the address the operator is looking at.
  const invalidFields = useInvalidFields();
  // Bound-aware inline additional-field errors from a failed create, keyed by
  // field name (mirrors the guest-detail edit path).
  const [additionalFieldErrors, setAdditionalFieldErrors] = useState<
    Record<string, string>
  >({});
  const clearFieldError = (fieldName: string) =>
    setAdditionalFieldErrors((prev) => {
      if (!(fieldName in prev)) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  // Soft required-field warning (non-blocking — admin may save anyway).
  const [requiredWarn, setRequiredWarn] = useState<Field[] | null>(null);
  // Soft workflow-overwrite preview (non-blocking).
  const [workflowWarn, setWorkflowWarn] = useState<OverwritePreview | null>(null);
  // "Open the layout builder?" — only asked when leaving would throw input away.
  const [confirmLayoutNav, setConfirmLayoutNav] = useState(false);
  // Inline "new custom field" editor (header "+").
  const [isFieldPopupOpen, setFieldPopupOpen] = useState(false);
  const { opening: openingField, open: openFieldEditor } =
    useLazyOpen(loadAdditionalField);
  // Kept as its own value (not inlined into useState) so the layout-builder
  // link can tell "nothing typed yet" from "would discard the admin's input" —
  // navigating away unmounts this modal and everything in it.
  const initialFields = useMemo<CreationFieldsRef>(
    () => ({
      guestType: mainGuestId || companion ? "companion" : "mainGuest",
      mainGuest: mainGuestId || "",
      firstName: "",
      lastName: "",
      gender: "",
      title: "",
      company: "",
      jobTitle: "",
      language: "",
      email: "",
      phone: "",
      mobile: "",
      address: "",
      address2: "",
      zipCode: "",
      city: "",
      country: "",
      username: "",
      code: "",
      emailOptIn: "",
      state: GuestState.UNCONTACTED,
      additionalFields: {},
    }),
    [companion, mainGuestId],
  );
  const [creationFields, setCreationFields] =
    useState<CreationFieldsRef>(initialFields);
  const hasInput =
    JSON.stringify(creationFields) !== JSON.stringify(initialFields);

  // ---- layout-builder entry -------------------------------------------------
  // Team-shared config, so the same gate the guest-detail popup uses: real
  // EDITOR+ (the narrow /detail-layouts endpoint 403s CUSTOM event roles) on a
  // writable event. Without an eventId there is no layout to edit.
  //
  // The link opens the version the dialog is CURRENTLY showing — landing on
  // Standard while the form on screen is "Messe-Kurzformular" would make the
  // builder look broken.
  const { selectedId: selectedLayoutId } = useCreateLayout(
    previewEventId ?? null,
    surface,
  );
  const layoutBuilderUrl = previewEventId
    ? `/guest-create-layout-builder/${previewEventId}/${slugOfCreateSurface(surface)}${
        selectedLayoutId && selectedLayoutId !== EDL_STANDARD
          ? `/${selectedLayoutId}`
          : ""
      }`
    : null;
  const canEditLayout =
    !!layoutBuilderUrl &&
    legacyRoleFloor !== "VIEWER" &&
    role !== "CUSTOM" &&
    !isArchived &&
    !isPlanLocked;
  const openLayoutBuilder = () => {
    if (!layoutBuilderUrl) return;
    // Unlike the detail popup, this dialog holds unsaved input — ask first
    // rather than silently dropping it on the way to the builder.
    if (hasInput) {
      setConfirmLayoutNav(true);
      return;
    }
    navigate(layoutBuilderUrl);
  };

  // Live inline-badge map: which entered fields a GUEST_CREATED automation will
  // set/overwrite, recomputed as the admin types. (previousGuest=null on create.)
  const overwriteMap = useMemo(() => {
    const preview = getOverwrites(
      creationFields as unknown as Record<string, unknown>,
      null,
      EmailTrigger.GUEST_CREATED,
    );
    return new Map<string, OverwriteEntry>(
      preview.entries.map((e) => [e.field, e]),
    );
  }, [getOverwrites, creationFields]);

  // Gate between the required-field warn and the actual create: preview how
  // GUEST_CREATED workflows (UPDATE_GUEST actions) will overwrite the entered
  // fields. Soft — the admin may save anyway.
  const proceedWithWorkflowCheck = () => {
    const preview = getOverwrites(
      creationFields as unknown as Record<string, unknown>,
      null,
      EmailTrigger.GUEST_CREATED,
    );
    // Admin opted out of this warning for this event → create straight through.
    if (preview.entries.length > 0 && !isWorkflowOverwriteWarnHidden(previewEventId)) {
      setWorkflowWarn(preview);
      return;
    }
    void proceedSave();
  };

  // A refused create, in words. Through `translateBackendError` with the
  // whole error, so the backend's `detail` travels: an undeliverable address
  // comes back as `emailInvalid` with the rule's reason in it (for this door
  // a list element — `invisible;index=0`), and `t2(code)` alone answered
  // every such refusal with the generic "Invalid email address".
  const describeCreateError = (error: unknown, code: string) =>
    translateBackendError(t2, error instanceof Error ? error : code);

  const proceedSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setAdditionalFieldErrors({});
      if (
        creationFields.guestType === "companion" &&
        !creationFields.mainGuest
      ) {
        setError(t2("companionNeedsMainGuest"));
        return;
      }
      if (creationFields.guestType === "mainGuest") {
        creationFields.mainGuest = "";
      }
      await onCreation(creationFields);
      onClose();
    } catch (error) {
      let errorMessage = "unknown";
      if (error instanceof Error) errorMessage = error.message;
      // Event full → the 3-option capacity popup is now open in GuestsProvider.
      // Close this form silently; the admin makes the decision there.
      if (errorMessage === CAPACITY_DECISION_PENDING) {
        onClose();
        return;
      }
      // Route backend per-field additional-field violations to inline,
      // bound-aware messages under each input (see GuestActions for the
      // matching edit-path handling).
      const rich = error as Error & {
        violations?: Array<{ fieldKey: string; code: string }>;
      };
      if (rich.violations && rich.violations.length > 0) {
        const byName = new Map(
          allFields
            .filter((f) => f.additionalField)
            .map((f) => [f.name.toLowerCase(), f]),
        );
        const mapped: Record<string, string> = {};
        for (const v of rich.violations) {
          const f = byName.get(v.fieldKey.toLowerCase());
          if (!f || mapped[f.name]) continue;
          mapped[f.name] = describeAdditionalFieldViolation(
            f,
            v.code as AdditionalFieldErrorCode,
            t2,
            locale,
          );
        }
        const names = Object.keys(mapped);
        if (names.length > 0) {
          setAdditionalFieldErrors(mapped);
          setError(
            t2("additionalFieldsValidationFailedList", {
              fields: names.join(", "),
            }),
          );
        } else {
          setError(describeCreateError(error, errorMessage));
        }
      } else {
        setError(describeCreateError(error, errorMessage));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    // Wrapper only so an e2e assertion about "which fields does the CREATE
    // form show" can scope to this dialog — the same field labels also exist
    // in the guest-detail popup this can be opened from. `display: contents`
    // means it generates no box, so the Card stays the effective flex child.
    <div data-testid="create-guest-dialog" className="contents">
    <Card
      padding="p-4 sm:p-6"
      width="w-full sm:w-[95vw] sm:max-w-[min(50rem,calc(100vw-var(--ai-rail-push)-2rem))]"
      height={mainGuestId ? "grow" : "max-h-[95dvh] sm:max-h-150 h-[90dvh]"}
      // Docks to the bottom edge on a phone like every other dialog in the app
      // (PCreateContact, PFixedGuestDetails, the send dialogs): only the top
      // corners are rounded, so the card reads as a sheet rising out of the
      // bottom rather than a card floating with a gap under it.
      //
      // Passed as `rounded` rather than appended via `className`: the default
      // is `rounded-3xl`, a shorthand, and layering a top-only longhand on top
      // of it leaves the outcome to emit order. Replacing it says what is meant.
      rounded="rounded-t-2xl sm:rounded-2xl"
      customHeader={
        <div className="flex flex-row items-center w-full gap-2">
          <h2 className="h2">{t("createGuest")}</h2>
          <div className="ml-auto flex items-center gap-1 shrink-0">
            {/* Which VERSION of this create form. Renders nothing until the
                event has one, so an untouched event looks exactly as before.
                Locked once something is typed: a version that HIDES the field
                just filled in would keep saving an invisible value. */}
            <EntityDetailLayoutPicker
              eventId={previewEventId}
              entity={surface}
              disabled={hasInput}
              disabledTitle={tUnsaved("title")}
            />
            {legacyRoleFloor !== "VIEWER" && (
              <button
                type="button"
                data-testid="create-guest-add-field"
                title={t("additionalFields")}
                aria-label={t("additionalFields")}
                disabled={openingField}
                onClick={() => openFieldEditor(() => setFieldPopupOpen(true))}
                className="cursor-pointer p-1 font-tertiary hover:font-primary transition-colors disabled:opacity-50"
              >
                {openingField ? (
                  <IconLoader2 size={18} className="animate-spin" />
                ) : (
                  <IconPlus size={18} />
                )}
              </button>
            )}
            {canEditLayout && (
              <button
                type="button"
                data-testid="create-layout-builder-open"
                title={tLayout("layoutEdit")}
                aria-label={tLayout("layoutEdit")}
                onClick={openLayoutBuilder}
                className="cursor-pointer p-1 font-tertiary hover:font-primary transition-colors"
              >
                <IconLayoutGrid size={18} />
              </button>
            )}
            {mainGuestId && (
              <button
                type="button"
                className="cursor-pointer p-1"
                onClick={onClose}
                aria-label={t("cancel")}
              >
                <IconX size={18} className="font-secondary" />
              </button>
            )}
          </div>
        </div>
      }
      className="rounded-t-2xl sm:rounded-2xl"
      justify=""
      shadow=""
    >
      {freeContingent !== undefined && freeContingent <= 0 && (
        <div className="mt-4 rounded-xl surface-error border outline-quaternary px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <IconAlertTriangle size={16} className="font-error shrink-0" />
            <span className="body-3-medium font-error">
              {t("noContingentWarning")}
            </span>
          </div>
          {eventCompany.isCompanyOwner ? (
            <Link to="/user/subscription">
              <PrimaryButton text={t("upgrade")} size="small" width="w-auto" color="surface-error-dark" />
            </Link>
          ) : eventCompany.status === "ready" && eventCompany.companyName ? (
            <span className="body-3-regular font-error">
              {tGate("askCompanyToUpgrade", { company: eventCompany.companyName })}
            </span>
          ) : null}
        </div>
      )}
      <WorkflowOverwriteProvider value={overwriteMap}>
        <div className="flex flex-col mt-4 pb-2 flex-1 min-h-0 overflow-auto custom-scrollbar">
          {/* "We'll be four" — the count is known before the names are, so the
              operator can mint the rows now and name them later. Above the
              form, not below it: it is the shortcut PAST the form. */}
          {onCreatePlaceholdersAction && legacyRoleFloor !== "VIEWER" && (
            <PlaceholderCompanions
              onCreateAction={onCreatePlaceholdersAction}
              onDoneAction={onClose}
              // A successful quick-add closes this form, and closing unmounts
              // everything typed into it with no draft anywhere — the same
              // reason the layout-builder jump asks first.
              confirmDiscard={hasInput}
              disabled={freeContingent !== undefined && freeContingent <= 0}
            />
          )}
          {/* Layout-driven form. Without a stored layout for this lane the
              resolver builds the default one, which mirrors the hardcoded
              Allgemeines | Kontakt + Zusatzfelder stack this replaced. */}
          <GuestDetailLayoutView
            surface={surface}
            eventId={previewEventId}
            creationFields={creationFields}
            setCreationFieldsAction={setCreationFields}
            additionalFieldErrors={additionalFieldErrors}
            onClearFieldError={clearFieldError}
            onFieldInvalid={invalidFields.onFieldInvalid}
            guestTypeLocked={companion || mainGuest}
          />
        </div>
      </WorkflowOverwriteProvider>

      {error && <p className="body-3-regular font-error mt-3">{error}</p>}
      <div className="flex flex-row space-x-3 w-full shrink-0 mt-4 pt-4">
        <SecondaryButton text={t("cancel")} onClick={onClose} />
        <PrimaryButton
          text={t("createGuest")}
          loading={loading}
          onClick={() => {
            // A box that shows an address the form does not hold: refuse, and
            // say so where the other create errors appear. The box itself is
            // already red — the click that landed here blurred it first.
            if (invalidFields.has()) {
              setError(t2("emailInvalid"));
              return;
            }
            // Soft-warn first if required fields are empty; the admin may still
            // proceed. Hidden/locked/defaulted required fields are skipped.
            const missing = getMissingRequiredFields(creationFields, allFields);
            if (missing.length > 0) {
              setRequiredWarn(missing);
              return;
            }
            proceedWithWorkflowCheck();
          }}
        />
      </div>
      {requiredWarn && (
        <RequiredFieldsWarnDialog
          fields={requiredWarn}
          loading={loading}
          onClose={() => setRequiredWarn(null)}
          onConfirm={() => {
            setRequiredWarn(null);
            proceedWithWorkflowCheck();
          }}
        />
      )}
      {/* Local Suspense so the first-load chunk doesn't bubble up to App.tsx's
          covering spinner (same pattern the old AdditionalFields block used). */}
      {isFieldPopupOpen && (
        <Suspense fallback={null}>
          <PAdditionalField setIsPopupOpen={setFieldPopupOpen} eventId={previewEventId} />
        </Suspense>
      )}
      {confirmLayoutNav && layoutBuilderUrl && (
        <PDeleteConfirmation
          title={tLayout("createLayoutDiscardTitle")}
          message={tLayout("createLayoutDiscardBody")}
          primaryButtonText={tLayout("createLayoutDiscardConfirm")}
          isLoading={false}
          onCloseAction={() => setConfirmLayoutNav(false)}
          onConfirmAction={async () => {
            setConfirmLayoutNav(false);
            navigate(layoutBuilderUrl);
          }}
        />
      )}
      {workflowWarn && (
        <WorkflowOverwriteDialog
          preview={workflowWarn}
          loading={loading}
          onClose={() => setWorkflowWarn(null)}
          onConfirm={(dontShowAgain) => {
            if (dontShowAgain) setWorkflowOverwriteWarnHidden(previewEventId);
            setWorkflowWarn(null);
            void proceedSave();
          }}
        />
      )}
    </Card>
    </div>
  );
};
